/**
 * The aperture oscilloscope, and the instrument LAG is calibrated on.
 *
 * The last few seconds of aperture, with a dashed marker at `now - LAG` — the
 * moment currently being rendered. Say a vowel and watch whether the marker
 * passes over the peak at the instant the letters swell. That comparison is far
 * faster and far less self-deceiving than judging letterforms, because a
 * misalignment of 150 ms is obvious as two marks failing to coincide and nearly
 * invisible as slightly-wrong type.
 *
 * The word bindings are drawn on the same axis, which makes this Phase 2's
 * instrument too: a word whose bar does not sit under the aperture excursion
 * that produced it is a binding that went to the wrong moment.
 */

import { config } from '../core/config.js';

const COLOURS = {
  grid: 'rgba(56, 54, 58, 0.9)',
  trace: '#d8a657',
  fill: 'rgba(216, 166, 87, 0.12)',
  marker: '#e07a5f',
  word: 'rgba(163, 197, 133, 0.85)',
  wordFinal: 'rgba(163, 197, 133, 0.45)',
  text: '#8b8880',
};

export class Trace {
  /** @param {HTMLCanvasElement} canvas */
  constructor(canvas) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
  }

  /**
   * @param {object} o
   * @param {import('../core/ringbuffer.js').RingBuffer} o.mouthBuffer
   * @param {number} o.now              performance.now()
   * @param {import('../core/types.js').Word[]} [o.words]
   */
  draw({ mouthBuffer, now, words = [] }) {
    const { ctx, canvas } = this;
    const w = canvas.width;
    const h = canvas.height;
    const span = config.render.TRACE_SECONDS * 1000;
    const t0 = now - span;

    const x = (t) => ((t - t0) / span) * w;
    const y = (v) => h - 6 - v * (h - 30);

    ctx.clearRect(0, 0, w, h);

    // Gridlines at each second, so the marker's offset is readable as a
    // distance rather than guessed.
    ctx.strokeStyle = COLOURS.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let s = Math.ceil(t0 / 1000) * 1000; s < now; s += 1000) {
      ctx.moveTo(Math.round(x(s)) + 0.5, 0);
      ctx.lineTo(Math.round(x(s)) + 0.5, h);
    }
    ctx.stroke();

    // The trace itself, drawn through the samples that exist rather than
    // sampled per pixel column.
    //
    // It used to call sampleAt once per pixel — twelve hundred binary searches
    // and twelve hundred interpolated objects per frame, sixty times a second,
    // to draw a line through fifty real points. That was a measurable part of
    // why the render loop fell from 30 Hz to 13 Hz when Phase 4 landed.
    //
    // Walking the buffer is also the more honest picture: the dots below are
    // the frames the camera actually delivered, so a stalled or slow camera
    // shows up as gaps in the trace instead of as a smooth line through
    // nothing. The per-pixel interpolation is still demonstrated, deliberately
    // and in isolation, by the ramp panel.
    const points = [];
    for (let i = 0; i < mouthBuffer.length; i++) {
      const s = mouthBuffer.at(i);
      if (s.t < t0 || s.t > now) continue;
      points.push([x(s.t), y(s.aperture ?? 0)]);
    }

    if (points.length > 1) {
      ctx.beginPath();
      ctx.moveTo(points[0][0], h);
      for (const [px, py] of points) ctx.lineTo(px, py);
      ctx.lineTo(points.at(-1)[0], h);
      ctx.closePath();
      ctx.fillStyle = COLOURS.fill;
      ctx.fill();

      ctx.beginPath();
      for (const [i, [px, py]] of points.entries()) {
        if (i === 0) ctx.moveTo(px, py);
        else ctx.lineTo(px, py);
      }
      ctx.strokeStyle = COLOURS.trace;
      ctx.lineWidth = 1.5;
      ctx.stroke();

      // One dot per delivered frame. Their spacing is the camera's real frame
      // rate, which is a number this project keeps needing and keeps having to
      // go and look up.
      ctx.fillStyle = COLOURS.trace;
      for (const [px, py] of points) ctx.fillRect(px - 1, py - 1, 2, 2);
    }

    // Word bindings, as bars along the bottom over the span each word claims.
    for (const word of words) {
      const from = word.tBind;
      const to = word.tBind + word.dur;
      if (to < t0 || from > now) continue;
      const x0 = Math.max(0, x(from));
      const x1 = Math.min(w, x(to));
      ctx.fillStyle = word.isFinal ? COLOURS.wordFinal : COLOURS.word;
      ctx.fillRect(x0, h - 5, Math.max(1, x1 - x0), 3);
    }

    // The moment being rendered right now. If this does not sit over the peak
    // when the letters swell, LAG is wrong — that is the entire calibration.
    const markerT = now - config.LAG;
    const mx = Math.round(x(markerT)) + 0.5;
    ctx.strokeStyle = COLOURS.marker;
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);
    ctx.beginPath();
    ctx.moveTo(mx, 0);
    ctx.lineTo(mx, h);
    ctx.stroke();
    ctx.setLineDash([]);

    ctx.fillStyle = COLOURS.marker;
    ctx.font = '11px ui-monospace, monospace';
    ctx.fillText(`now − LAG (${config.LAG}ms)`, Math.min(mx + 5, w - 130), 13);

    ctx.fillStyle = COLOURS.text;
    ctx.fillText(`${config.render.TRACE_SECONDS}s of aperture`, 6, h - 10);
  }
}

export default Trace;
